import { useMemo, useState } from "react";
import { Checkbox, Dialog, FormControlLabel, IconButton, InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "assets/icons/close.svg?react";
import budgetService from "api/budgetService";
import { budgetDialogPaperProps } from "components/Budget/budgetDialog";
import { IBudgetExpense, IBudgetExpenseQuota } from "dto/Budget/IBudgetExpense";
import { formatMoney } from "utils/formatMoney";
import styles from "./styles.module.scss";

interface Props {
  expense: IBudgetExpense;
  onClose: () => void;
}

const TABS = ["Zestawienie", "Zaangażowania", "Faktury", "Umowy"] as const;

const matches = (search: string, ...fields: (string | undefined)[]) => {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => (f ?? "").toLowerCase().includes(q));
};

const BudgetExpensePreviewModal = ({ expense, onClose }: Props) => {
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const [groupInvoices, setGroupInvoices] = useState(false);

  const { data: divisions = [] } = budgetService.useGetDivisions();
  const { data: chapters = [] } = budgetService.useGetChapters(expense.DivisionId);
  const { data: paragraphs = [] } = budgetService.useGetParagraphs();
  const { data: engagements = [] } = budgetService.useGetEngagements(expense.Id);
  const { data: invoices = [] } = budgetService.useGetInvoices(expense.Id);
  const { data: contracts = [] } = budgetService.useGetContracts(expense.Id);

  const division = divisions.find((d) => d.Id === expense.DivisionId);
  const chapter = chapters.find((c) => c.Id === expense.ChapterId);

  const quotaSymbol = (q: IBudgetExpenseQuota) => {
    const p = paragraphs.find((x) => x.Id === q.ParagraphId);
    const base = p?.Symbol ?? "?";
    return `§${base}${q.ParagraphSuffix !== "0" ? q.ParagraphSuffix : ""}`;
  };

  const engagedByQuota = useMemo(() => {
    const map: Record<string, number> = {};
    engagements
      .flatMap((e) => e.Quotas)
      .forEach((q) => {
        map[q.ExpenseQuotaId] = (map[q.ExpenseQuotaId] ?? 0) + q.Amount;
      });
    return map;
  }, [engagements]);

  const settledByQuota = useMemo(() => {
    const map: Record<string, number> = {};
    invoices
      .flatMap((i) => i.Quotas)
      .forEach((q) => {
        map[q.ExpenseQuotaId] = (map[q.ExpenseQuotaId] ?? 0) + q.Amount;
      });
    return map;
  }, [invoices]);

  const plannedTotal = expense.PlannedAmount || 1;

  const gridStyle = {
    gridTemplateColumns: `minmax(220px, 1fr) 190px 56px ${expense.Quotas.map(
      () => "minmax(110px, 130px)",
    ).join(" ")}`,
  };

  const sumQuotas = (by: Record<string, number>) =>
    expense.Quotas.reduce((s, q) => s + (by[q.Id] ?? 0), 0);

  const summaryRows = [
    {
      key: "planned",
      name: "WYDATEK PLANOWANY",
      total: expense.PlannedAmount,
      amount: (q: IBudgetExpenseQuota) => q.Amount,
      fill: styles.fillPlanned,
    },
    {
      key: "engaged",
      name: "ZAANGAŻOWANIA RAZEM",
      total: sumQuotas(engagedByQuota),
      amount: (q: IBudgetExpenseQuota) => engagedByQuota[q.Id] ?? 0,
      fill: styles.fillEngaged,
    },
    {
      key: "settled",
      name: "WYKONANIE",
      total: sumQuotas(settledByQuota),
      amount: (q: IBudgetExpenseQuota) => settledByQuota[q.Id] ?? 0,
      fill: styles.fillSettled,
    },
    {
      key: "free",
      name: "WOLNE ŚRODKI",
      total: expense.Quotas.reduce(
        (s, q) => s + (q.Amount - (engagedByQuota[q.Id] ?? 0)),
        0,
      ),
      amount: (q: IBudgetExpenseQuota) => q.Amount - (engagedByQuota[q.Id] ?? 0),
      fill: styles.fillFree,
    },
  ].filter((r) => matches(search, r.name));

  const visibleEngagements = engagements.filter((e) =>
    matches(search, e.Title, e.Signature, e.Status),
  );
  const visibleInvoices = invoices.filter((i) =>
    matches(search, i.Title, i.Number, i.Sender),
  );
  const visibleContracts = contracts.filter((c) =>
    matches(search, c.Title, c.Sender),
  );

  const quotaHeader = (
    <>
      {expense.Quotas.map((q) => (
        <span key={q.Id} className={styles.quotaHead}>
          {quotaSymbol(q)}
        </span>
      ))}
    </>
  );

  const emptyState = <div className={styles.empty}>Lista jest pusta</div>;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={budgetDialogPaperProps}
    >
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <span className={styles.title}>{expense.Title}</span>
          <IconButton onClick={onClose} size="small" className={styles.close}>
            <CloseIcon className={styles.closeIcon} />
          </IconButton>
        </div>

        <div className={styles.subheader}>
          <div className={styles.classification}>
            {division && (
              <span>
                {division.Symbol} – {division.Name}
              </span>
            )}
            {chapter && (
              <span>
                {chapter.Symbol} – {chapter.Name}
              </span>
            )}
          </div>
          <TextField
            size="small"
            placeholder="Szukaj na liście"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchBox}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div className={styles.tabs}>
          {TABS.map((label, i) => (
            <button
              key={label}
              className={`${styles.tab} ${tab === i ? styles.tabActive : ""}`}
              onClick={() => setTab(i)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className={styles.body}>
          {tab === 0 && (
            <div className={styles.list}>
              <div className={`${styles.rowHead}`} style={gridStyle}>
                <span />
                <span className={styles.right}>Kwota</span>
                <span className={styles.right}>w tym:</span>
                {quotaHeader}
              </div>
              {summaryRows.map((row) => (
                <div className={styles.row} style={gridStyle} key={row.key}>
                  <span className={styles.rowName}>{row.name}</span>
                  <div className={styles.amountCell}>
                    <span>{formatMoney(row.total)}</span>
                    <div className={styles.bar}>
                      <div
                        className={`${styles.fill} ${row.fill}`}
                        style={{
                          width: `${Math.min(100, (row.total / plannedTotal) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                  <span />
                  {expense.Quotas.map((q) => (
                    <span key={q.Id} className={styles.right}>
                      {formatMoney(row.amount(q))}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          )}

          {tab === 1 && (
            <div className={styles.list}>
              <div className={styles.rowHead} style={gridStyle}>
                <span />
                <span className={styles.right}>Kwota</span>
                <span className={styles.right}>w tym:</span>
                {quotaHeader}
              </div>
              {visibleEngagements.length === 0
                ? emptyState
                : visibleEngagements.map((eng) => {
                    const byQuota: Record<string, number> = {};
                    eng.Quotas.forEach((q) => {
                      byQuota[q.ExpenseQuotaId] =
                        (byQuota[q.ExpenseQuotaId] ?? 0) + q.Amount;
                    });
                    return (
                      <div className={styles.row} style={gridStyle} key={eng.Id}>
                        <div className={styles.titleCell}>
                          <span className={styles.itemTitle}>{eng.Title}</span>
                          {eng.Signature && (
                            <span className={styles.itemSub}>{eng.Signature}</span>
                          )}
                        </div>
                        <div className={styles.amountCell}>
                          <span>{formatMoney(eng.Amount)}</span>
                          {eng.Accepted ? (
                            <div className={styles.bar}>
                              <div
                                className={`${styles.fill} ${styles.fillSettled}`}
                                style={{
                                  width: `${Math.min(100, (eng.Amount / plannedTotal) * 100)}%`,
                                }}
                              />
                            </div>
                          ) : null}
                        </div>
                        <span className={styles.status}>
                          {eng.Accepted ? "" : eng.Status}
                        </span>
                        {expense.Quotas.map((q) => {
                          const value = byQuota[q.Id] ?? 0;
                          const boxed = value > 0;
                          return (
                            <span key={q.Id} className={styles.right}>
                              <span
                                className={
                                  boxed
                                    ? eng.Accepted
                                      ? styles.boxAccepted
                                      : styles.boxPending
                                    : ""
                                }
                              >
                                {formatMoney(value)}
                              </span>
                            </span>
                          );
                        })}
                      </div>
                    );
                  })}
            </div>
          )}

          {tab === 2 && (
            <div className={styles.list}>
              <div className={styles.invoiceToolbar}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={groupInvoices}
                      onChange={(e) => setGroupInvoices(e.target.checked)}
                    />
                  }
                  label="Grupuj"
                />
              </div>
              <div className={styles.rowHead} style={gridStyle}>
                <span />
                <span className={styles.right}>Nr/Kwota</span>
                <span className={styles.right}>w tym:</span>
                {quotaHeader}
              </div>
              {visibleInvoices.length === 0
                ? emptyState
                : visibleInvoices.map((inv) => {
                    const byQuota: Record<string, number> = {};
                    inv.Quotas.forEach((q) => {
                      byQuota[q.ExpenseQuotaId] =
                        (byQuota[q.ExpenseQuotaId] ?? 0) + q.Amount;
                    });
                    return (
                      <div className={styles.row} style={gridStyle} key={inv.Id}>
                        <div className={styles.titleCell}>
                          <span className={styles.itemTitle}>{inv.Title}</span>
                          {inv.Sender && (
                            <span className={styles.itemSub}>{inv.Sender}</span>
                          )}
                        </div>
                        <div className={styles.nrAmountCell}>
                          <span>{inv.Number}</span>
                          <span>{formatMoney(inv.Amount)}</span>
                        </div>
                        <span />
                        {expense.Quotas.map((q) => (
                          <span key={q.Id} className={styles.right}>
                            {formatMoney(byQuota[q.Id] ?? 0)}
                          </span>
                        ))}
                      </div>
                    );
                  })}
            </div>
          )}

          {tab === 3 && (
            <div className={styles.list}>
              <div
                className={styles.rowHead}
                style={{ gridTemplateColumns: "minmax(220px, 1fr) 160px" }}
              >
                <span />
                <span className={styles.right}>Wartość</span>
              </div>
              {visibleContracts.length === 0
                ? emptyState
                : visibleContracts.map((c) => (
                    <div
                      className={styles.row}
                      style={{ gridTemplateColumns: "minmax(220px, 1fr) 160px" }}
                      key={c.Id}
                    >
                      <div className={styles.titleCell}>
                        <span className={styles.itemTitle}>{c.Title}</span>
                        {c.Sender && (
                          <span className={styles.itemSub}>{c.Sender}</span>
                        )}
                      </div>
                      <span className={styles.right}>{formatMoney(c.Value)}</span>
                    </div>
                  ))}
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default BudgetExpensePreviewModal;
